import { keyframes, styled } from "goober";
import { Button } from "../Button";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";

// Strips Tooltip’s dark label chrome so the card supplies its own surface.
export const CONNECT_PROVIDER_TOOLTIP_STYLE = {
  background: "transparent",
  boxShadow: "none",
  padding: 0,
  maxWidth: "none",
  color: "var(--text-primary)",
  "--text-secondary": "inherit",
} as React.CSSProperties;

const Card = styled("div")`
  width: min(320px, calc(100vw / var(--ui-zoom, 1) - 24px));
  padding: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
`;

const float = keyframes`
  from {
    transform: translate(var(--x), calc(var(--y) + 2px)) rotate(var(--r));
  }
  to {
    transform: translate(var(--x), calc(var(--y) - 2px))
      rotate(calc(var(--r) + 1.5deg));
  }
`;

const drift = keyframes`
  from {
    transform: translateX(-6px) scale(1);
  }
  to {
    transform: translateX(6px) scale(1.06);
  }
`;

const Hero = styled("div")`
  position: relative;
  height: 128px;
  border-radius: 8px;
  background: var(--surface-secondary);
  overflow: hidden;
`;

const Glow = styled("div")`
  position: absolute;
  inset: -24px;
  background:
    radial-gradient(circle at 34% 58%, rgb(66 133 244 / 0.28), transparent 26%),
    radial-gradient(
      circle at 40% 40%,
      rgb(155 114 203 / 0.22),
      transparent 24%
    ),
    radial-gradient(circle at 50% 50%, rgb(217 119 87 / 0.3), transparent 28%),
    radial-gradient(circle at 66% 56%, rgb(16 163 127 / 0.24), transparent 26%);
  filter: blur(14px);
  animation: ${drift} 7000ms ease-in-out infinite alternate;
`;

const LogoCard = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -30px 0 0 -30px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  transform: translate(var(--x), var(--y)) rotate(var(--r));
  animation: ${float} 3600ms ease-in-out infinite alternate;
`;

const Content = styled("div")`
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConnectButton = styled(Button)`
  width: 100%;
  justify-content: center;
  margin-top: 12px;
  border-radius: 9999px;
`;

export function ConnectProviderCard({
  usage,
  onConnect,
}: {
  usage: string | null;
  onConnect: () => void;
}) {
  return (
    <Card>
      <Hero>
        <Glow />
        <LogoCard
          style={
            {
              "--x": "-46px",
              "--y": "4px",
              "--r": "-8deg",
              animationDelay: "-1200ms",
            } as React.CSSProperties
          }
        >
          <ProviderLogo provider="googleGemini" size={28} />
        </LogoCard>
        <LogoCard
          style={
            {
              "--x": "46px",
              "--y": "4px",
              "--r": "8deg",
              animationDelay: "-2400ms",
            } as React.CSSProperties
          }
        >
          <ProviderLogo provider="openai" size={28} />
        </LogoCard>
        <LogoCard
          style={
            {
              "--x": "0px",
              "--y": "-4px",
              "--r": "0deg",
              zIndex: 1,
              boxShadow: "var(--elevation-high)",
            } as React.CSSProperties
          }
        >
          <ProviderLogo provider="anthropic" size={30} />
        </LogoCard>
      </Hero>
      <Content>
        <Text variant="text-regular-md">
          Keep building with your own AI provider
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          {usage ?? "Connect Anthropic, OpenAI, or another provider."}
        </Text>
        <ConnectButton variant="primary" size="l" onClick={onConnect}>
          Connect AI provider
        </ConnectButton>
      </Content>
    </Card>
  );
}
